import React from 'react';
import { Box, Container, Heading, VStack, Image, Text, Skeleton, LinkBox, HStack, Select, Badge, useColorModeValue, Input, InputGroup, InputLeftElement, InputRightElement, IconButton, Grid, GridItem, useMediaQuery, Tooltip } from '@chakra-ui/react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getArticles, Article, Paginated } from '../services/articles';
import { getBanners, Banner as UIBanner } from '../services/banners';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { assetUrl } from '../utils/url';
import MainLayout from '../components/layout/MainLayout';
import { getCategories, CategoryItem } from '../services/categories';
import NewsletterCTA from '../components/common/NewsletterCTA';
import { Eye, Clock, Search, X } from 'lucide-react';
import InstagramGeneratorButton from '../components/admin/InstagramGeneratorButton';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const BlogTile: React.FC<{ article: Article; variant?: 'large' | 'small' }> = ({ article, variant }) => {
  const { t } = useTranslation();
  const link = article.slug ? `/news/${article.slug}` : `/articles/${article.id}`;
  const readTime = article.read_time || article.estimated_read_minutes;
  const viewCount = article.view_count;
  const bgColor = useColorModeValue('white', 'gray.800');
  const categoryName = (article as any)?.category?.name || (article as any)?.category_name;
  const imageH = variant === 'large'
    ? ({ base: '280px', md: '360px' } as const)
    : variant === 'small'
    ? ({ base: '160px', md: '180px' } as const)
    : ({ base: '200px', md: '220px' } as const);

  const publishedAt = (article as any).published_at || (article as any).created_at;
  const publishedDateStr = publishedAt ? (()=>{ try { return new Date(publishedAt).toLocaleDateString('cs-CZ'); } catch { return ''; } })() : '';

  return (
    <LinkBox
      as={RouterLink}
      to={link}
      borderRadius="md"
      overflow="hidden"
      bg={bgColor}
      borderWidth="0"
      _hover={{ boxShadow: 'xl', transform: 'translateY(-3px)' }}
      transition="all 0.25s ease"
      position="relative"
    >
      <Box position="relative">
        <Image
          src={assetUrl(article.image_url) || '/stadium-placeholder.jpg'}
          alt={article.title}
          w="100%"
          h={imageH}
          objectFit="cover"
          loading={variant === 'large' ? 'eager' : 'lazy'}
          decoding="async"
          sizes={variant === 'large' ? '(min-width: 768px) 60vw, 100vw' : '100vw'}
          fetchPriority={variant === 'large' ? 'high' as any : 'auto' as any}
        />
        <Box position="absolute" inset={0} bgGradient="linear(to-t, rgba(0,0,0,0.55), rgba(0,0,0,0.15))" />
        {/* Top info row: category (left), date (center), read time (right) */}
        <HStack position="absolute" top={2} left={2} right={2} justify="space-between" align="center">
          {categoryName ? (
            <Tooltip label={t('blog.category')} hasArrow>
              <Badge bg="rgba(0,0,0,0.7)" color="white" fontSize="xs" px={2} py={1} borderRadius="md">
                {categoryName}
              </Badge>
            </Tooltip>
          ) : <Box />}
          {publishedDateStr ? (
            <Tooltip label={t('blog.publish_date')} hasArrow>
              <Badge bg="rgba(0,0,0,0.7)" color="white" fontSize="xs" px={2} py={1} borderRadius="md">
                {publishedDateStr}
              </Badge>
            </Tooltip>
          ) : <Box />}
          {readTime ? (
            <Tooltip label={t('blog.read_time')} hasArrow>
              <Badge display="flex" alignItems="center" gap={1} bg="rgba(0,0,0,0.7)" color="white" fontSize="xs" px={2} py={1} borderRadius="md">
                <Clock size={12} />
                {readTime} min
              </Badge>
            </Tooltip>
          ) : <Box />}
        </HStack>

        <Heading
          as="h3"
          fontSize={variant === 'large' ? { base: 'xl', md: '2xl' } : { base: 'lg', md: 'xl' }}
          fontWeight="800"
          letterSpacing="0.3px"
          textTransform="uppercase"
          position="absolute"
          bottom={3}
          left={4}
          right={4}
          color="white"
          noOfLines={2}
        >
          {article.title}
        </Heading>
      </Box>
      <InstagramGeneratorButton
        article={article as any}
        targetUrl={typeof window !== 'undefined' ? new URL(link, window.location.origin).toString() : undefined}
        placement="inline"
        position="bottom-right"
        size="sm"
      />
    </LinkBox>
  );
};

const BlogPage: React.FC = () => {
  const { t } = useTranslation();
  const pageSize = 18;
  const [categories, setCategories] = React.useState<CategoryItem[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = React.useMemo(() => {
    const cid = searchParams.get('category_id');
    return cid ? Number(cid) : '';
  }, []);
  const [categoryId, setCategoryId] = React.useState<number | ''>(initialCategory);
  const month = searchParams.get('month') || '';
  const matchId = searchParams.get('match_id') || '';
  const qParam = searchParams.get('q') || '';
  const [qInput, setQInput] = React.useState<string>(qParam);
  const [matchInput, setMatchInput] = React.useState<string>('');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.500', 'gray.400');
  const suggestBg = useColorModeValue('white','gray.800');
  const suggestBorder = useColorModeValue('gray.200','gray.700');
  const suggestHoverBg = useColorModeValue('gray.50','gray.700');

  React.useEffect(() => {
    (async () => {
      try {
        const list = await getCategories();
        setCategories(list || []);
      } catch {}
    })();
  }, []);

  // Keep categoryId in sync with URL params (category_id preferred; fallback to category slug)
  React.useEffect(() => {
    const cid = searchParams.get('category_id');
    const slug = searchParams.get('category');
    if (cid !== null) {
      setCategoryId(cid ? Number(cid) : '');
      return;
    }
    if (slug) {
      // Try to resolve slug to id once categories are loaded
      const norm = decodeURIComponent(slug).toLowerCase();
      const found = categories.find((c) => (c as any)?.slug === slug || String(c.name).toLowerCase() === norm);
      setCategoryId(found?.id || '');
      return;
    }
    // No category param present → clear filter
    setCategoryId('');
  }, [searchParams, categories]);
  React.useEffect(() => {
    setQInput(qParam);
  }, [qParam]);

  // Match suggestions: only show matches that already have a blog (articles with match_snapshot)
  const matchSuggestQ = useQuery<Paginated<Article>>(
    ['blog-match-suggest', { q: matchInput }],
    () => getArticles({ page: 1, page_size: 50, published: true, q: matchInput }),
    { enabled: matchInput.trim().length >= 2 }
  );
  const matchSuggestions = React.useMemo(() => {
    const items = matchSuggestQ.data?.data || [];
    const uniq = new Map<string, any>();
    items.forEach((a: any) => {
      const ms = (a as any)?.match_snapshot;
      const id = String(ms?.external_match_id || '') || '';
      if (!id) return;
      if (!uniq.has(id)) uniq.set(id, { id, title: a.title, date: ms?.date_time || ms?.date, home: ms?.home, away: ms?.away, comp: ms?.competition || ms?.competitionName });
    });
    return Array.from(uniq.values()).slice(0, 10);
  }, [matchSuggestQ.data]);
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<Paginated<Article>>(
    ['articles-public', { page_size: pageSize, published: true, category_id: categoryId || undefined, month: month || undefined, match_id: matchId || undefined, q: qParam || undefined }],
    ({ pageParam = 1 }) =>
      getArticles({
        page: pageParam,
        page_size: pageSize,
        published: true,
        ...(categoryId ? { category_id: Number(categoryId) } : {}),
        ...(month ? { month } : {}),
        ...(matchId ? { match_id: matchId } : {}),
        ...(qParam ? { q: qParam } : {}),
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((sum, p) => sum + (p?.data?.length || 0), 0);
        if (!lastPage) return undefined;
        if (loaded < (lastPage.total || 0)) return allPages.length + 1;
        return undefined;
      },
    }
  );

  const articles = data?.pages?.flatMap((p) => p?.data || []) || [];
  const heroArticles = React.useMemo(() => articles.slice(0, 3), [articles]);
  const listArticles = React.useMemo(() => articles.slice(3), [articles]);

  // Fetch inline article banners (active, placement=article_inline)
  const articleBannersQ = useQuery<UIBanner[]>(
    ['banners', { placement: 'article_inline' }],
    () => getBanners({ active: true, placement: 'article_inline' }),
    { staleTime: 60 * 1000 }
  );
  const articleBanners = (articleBannersQ.data || []) as UIBanner[];
  // Decide insertion index depending on layout (1 column vs multi-column)
  const [isSmUp] = useMediaQuery('(min-width: 30em)'); // Chakra sm breakpoint (~480px)
  const insertionIndex = isSmUp ? 5 : 2;

  // Infinite scroll via intersection observer
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!hasNextPage || !sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Derive page SEO title/description
  const parts: string[] = [t('blog.title')];
  if (categoryId) {
    const cat = categories.find((c) => c.id === Number(categoryId));
    if (cat?.name) parts.push(cat.name);
  }
  if (month) parts.push(month);
  if (matchId) parts.push(t('blog.match'));
  if (qParam) parts.push(`Hledání: ${qParam}`);
  const pageTitle = parts.join(' · ');
  const pageDesc = qParam
    ? t('blog.search_results', { query: qParam })
    : categoryId
    ? t('blog.category_articles', { category: (categories.find((c) => c.id === Number(categoryId))?.name) || '' })
    : t('blog.latest_articles');
  const canonical = typeof window !== 'undefined' ? window.location.href : undefined;

  // Debounced search param update when typing
  React.useEffect(() => {
    const next: Record<string, string> = {};
    if (categoryId) next.category_id = String(categoryId);
    if (month) next.month = month;
    if (matchId) next.match_id = matchId;
    if (qInput) next.q = qInput;
    const id = window.setTimeout(() => setSearchParams(next), 400);
    return () => window.clearTimeout(id);
  }, [qInput, categoryId, month, matchId]);

  return (
    <MainLayout>
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      {canonical && <link rel="canonical" href={canonical} />}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: (articles || []).slice(0, 12).map((a, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            url: (typeof window !== 'undefined' ? window.location.origin : '') + (a.slug ? `/news/${a.slug}` : `/articles/${a.id}`),
            name: a.title,
          })),
        })}
      </script>
    </Helmet>
    <Box>
      {/* Header like blog.html */}
      <Box bg="transparent" color="inherit" py={{ base: 8, md: 10 }} mb={4} borderBottom="1px" borderColor={borderColor}>
        <Container maxW="7xl">
          <HStack justify="space-between" align="center" spacing={4} wrap="wrap">
            <Heading as="h1" size={{ base: 'xl', md: '2xl' }}>{t('blog.title')}</Heading>
            <HStack spacing={3} w={{ base: '100%', md: '620px' }}>
              <Box flex="1">
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Search size={16} />
                  </InputLeftElement>
                  <Input
                    placeholder={t('blog.search_articles')}
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                  />
                  {qInput && (
                    <InputRightElement>
                      <IconButton
                        aria-label="Clear search"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setQInput('');
                          const next: Record<string, string> = {};
                          if (categoryId) next.category_id = String(categoryId);
                          if (month) next.month = month;
                          if (matchId) next.match_id = matchId;
                          setSearchParams(next);
                        }}
                        icon={<X size={14} />}
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>
              {!!categories.length && (
                <Select
                  maxW={{ base: '48%', md: '220px' }}
                  placeholder={t('blog.all_categories')}
                  value={categoryId}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : '';
                    setCategoryId(val);
                    const next: Record<string, string> = {};
                    if (val) next.category_id = String(val);
                    if (qParam) next.q = qParam;
                    if (month) next.month = month;
                    if (matchId) next.match_id = matchId;
                    setSearchParams(next);
                  }}
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
              )}
              <Box flex={{ base: '1', md: '0 0 220px' }} position="relative">
                <InputGroup>
                  <Input
                    placeholder={t('blog.search_match')}
                    value={matchInput}
                    onChange={(e) => setMatchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const s = matchInput.trim();
                        if (/^\d+$/.test(s)) {
                          const next: Record<string, string> = {};
                          next.match_id = s;
                          if (categoryId) next.category_id = String(categoryId);
                          if (month) next.month = month;
                          if (qParam) next.q = qParam;
                          setSearchParams(next);
                        }
                      }
                    }}
                  />
                </InputGroup>
                {(matchInput.trim().length >= 2 && matchSuggestions.length > 0) && (
                  <Box position="absolute" top="100%" left={0} right={0} bg={suggestBg} borderWidth="1px" borderColor={suggestBorder} borderRadius="md" mt={1} zIndex={10} maxH="260px" overflowY="auto" boxShadow="lg">
                    {matchSuggestions.map((m: any) => (
                      <Box key={m.id} px={3} py={2} _hover={{ bg: suggestHoverBg }} cursor="pointer" onClick={() => {
                        const next: Record<string, string> = { match_id: String(m.id) };
                        if (categoryId) next.category_id = String(categoryId);
                        if (month) next.month = month;
                        if (qParam) next.q = qParam;
                        setSearchParams(next);
                        setMatchInput('');
                      }}>
                        <Text fontSize="sm" fontWeight="600" noOfLines={1}>{m.home} vs {m.away}</Text>
                        <Text fontSize="xs" color={textColor} noOfLines={1}>{m.date || ''}{m.comp ? ` • ${m.comp}` : ''}</Text>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </HStack>
          </HStack>
        </Container>
      </Box>

      {heroArticles.length > 0 && (
        <Container maxW="7xl" mb={6}>
          <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={6}>
            <GridItem>
              <BlogTile article={heroArticles[0]} variant="large" />
            </GridItem>
            <GridItem>
              <VStack spacing={6} align="stretch">
                {heroArticles.slice(1, 3).map((a) => (
                  <BlogTile key={a.id} article={a} variant="small" />
                ))}
              </VStack>
            </GridItem>
          </Grid>
        </Container>
      )}

      <Container maxW="7xl">
        {/* Responsive grid with consistent card sizing */}
        <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={8}>
          {isLoading && Array.from({ length: 9 }).map((_, i) => (
            <Skeleton key={i} h={{ base: '260px', md: '300px' }} borderRadius="md" />
          ))}
          {!isLoading && listArticles.map((a, idx) => (
            <React.Fragment key={`row-${a.id}`}>
              <GridItem>
                <BlogTile article={a} />
              </GridItem>
              {articleBanners.length > 0 && idx === insertionIndex && (
                <GridItem key={`banner-inline-${articleBanners[0].id}`} colSpan={{ base: 1, sm: 2, lg: 3 }}>
                  <a
                    href={articleBanners[0].click_url || '#'}
                    target={articleBanners[0].click_url ? '_blank' : undefined}
                    rel={articleBanners[0].click_url ? 'noopener noreferrer' : undefined}
                    style={{ display: 'block' }}
                  >
                    {/* eslint-disable-next-line jsx-a11y/alt-text */}
                    <img
                      src={assetUrl(articleBanners[0].image_url) || '/images/sponsors/placeholder.png'}
                      alt={articleBanners[0].name}
                      style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 8 }}
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                </GridItem>
              )}
            </React.Fragment>
          ))}
        </Grid>
        {isFetchingNextPage && (
          <VStack py={6}>
            <Text color={textColor}>{t('blog.loading')}</Text>
          </VStack>
        )}
      </Container>
      
      {/* Newsletter CTA */}
      <NewsletterCTA />
      
    </Box>
    </MainLayout>
  );
};

export default BlogPage;
